import { useCallback, useEffect, useMemo, useState } from 'react';
import './styles.css';
import { useDispatch, useSelector } from 'react-redux';
import { UIRootState } from 'ui/store';
import { translate } from 'game/translate';
import { getStringWithParams } from 'game/quests/utils';
import { toggleIsNewMission } from 'ui/store/actions/gameUIActions';
import { useDomAwareVisibility } from 'ui/hooks/useOnScreen';
import { GAEvent, GAEventNames } from 'game/analytics/events';
import { SoundManager } from 'game/core/soundManager';

interface MissionsButtonProps {
    type: 'main' | 'secondary';
    onClick: () => void;
    isSelected?: boolean;
}

export const MissionsButton: React.FC<MissionsButtonProps> = ({ onClick, type, isSelected }) => {
    const dispatch = useDispatch();
    const isNewMissionCompleted = useSelector((state: UIRootState) => state.gameUI.missions?.isNewMissionCompleted);
    const isNewMissionCompletedOnMounted = useSelector((state: UIRootState) => state.gameUI.missions?.isNewMissionCompletedOnMounted);
    const dailyMission = useSelector((state: UIRootState) => state.gameUI.missions?.dailyMission);
    const isAllMissionsCompleted = useMemo(() => !dailyMission?.rewardsClaimed && !dailyMission?.actions.find(x => !x.passed), [dailyMission]);

    const dailyMissionCompletedCount = useMemo(() => dailyMission?.actions.filter(x => x.passed).length || 0, [dailyMission]);
    const dailyMissionTotalCount = useMemo(() => dailyMission?.actions.length || 0, [dailyMission]);

    const isMissionsVisited = localStorage.getItem('missions-visited') == 'true';

    const [isTooltipVisible, setIsTooltipVisible] = useState(false);
    const [isForceCheckedIcon, setIsForceCheckedIcon] = useState(false);
    const [tooltipText, setTooltipText] = useState("");

    const { ref, isVisible } = useDomAwareVisibility(700);

    const handleMissionsClick = useCallback(() => {
        localStorage.setItem('missions-visited', 'true');
        dispatch(toggleIsNewMission(false));
        setIsForceCheckedIcon(false);
        GAEvent(GAEventNames.MISSION_BUTTON_CLICK);
        onClick();
    }, [onClick, dispatch, toggleIsNewMission]);

    const missionsIcon = useMemo(() => {
        const chestImage = (
            <>
                <picture>
                    <source srcSet={`/assets/missions/small-particles.webp`} type="image/webp" />
                    <img className={`mission-button-particles--${type}`} style={{ height: '35px', zIndex: 0, position: 'absolute', top: '10px', left: '32px' }} src={`/assets/missions/small-particles.png`}/>
                </picture>
                <picture>
                    <img style={{ height: '50px', zIndex: 1, position: 'relative', top: '2px', left: '1px' }} src={`/assets/missions/complet_all_chest_image.png`}/>
                </picture>
            </>
        );
        
        if (isAllMissionsCompleted) {
            return chestImage;
        }

        if ((isNewMissionCompleted || isForceCheckedIcon) && !isAllMissionsCompleted) {
            return (
                <picture>
                    <img id="closing-ignore-el" style={{ height: '50px' }} src={`/assets/missions/mission_checked_icon.png`} alt="Missions" />
                </picture>
            );
        }

        const defaultImage = (
            <picture>
                    <source srcSet={`/assets/menu/icon_missions.webp`} type="image/webp" />
                    <img style={{ height: '50px' }} src={`/assets/menu/icon_missions.png`} alt="Missions" />
            </picture>
        );

        if (!isNewMissionCompleted && !isAllMissionsCompleted) {
            return defaultImage;
        }

        return defaultImage;
    }, [isNewMissionCompleted, isAllMissionsCompleted, dailyMission?.isAdditionalMission, isForceCheckedIcon]);

    const handleShowTooltipData = useCallback((show: boolean) => {
        if (isAllMissionsCompleted && show) {
            setTooltipText(translate('mission_button_tooltip_text_completed'));
            return;
        }
        
        if(!isAllMissionsCompleted && show) {
            setTooltipText(getStringWithParams(translate('mission_button_tooltip_text_progress'), dailyMissionCompletedCount.toString(), dailyMissionTotalCount.toString()));
            return;
        }
    }, [isAllMissionsCompleted, dailyMissionCompletedCount, dailyMissionTotalCount]);

    const handleShowDelayedTooltip = useCallback(() => {
        setIsTooltipVisible(true);
        handleShowTooltipData(true);
        setIsForceCheckedIcon(true);
        
        const closeTooltip = () => {
            dispatch(toggleIsNewMission(false));
            setIsForceCheckedIcon(false);
            document.removeEventListener('click', closeTooltip);
        };

        document.addEventListener('click', closeTooltip);

        setTimeout(() => {
            closeTooltip();
        }, 5000);
    }, [handleShowTooltipData, dispatch]);

    useEffect(() => {
        if (isVisible && !!isNewMissionCompletedOnMounted) {
            handleShowDelayedTooltip();
        }
    }, [isVisible]);

    useEffect(() => {
        if (isNewMissionCompletedOnMounted) {

            if (isVisible) {
                handleShowDelayedTooltip();
            }

            return;
        }

        setIsTooltipVisible(!!isNewMissionCompleted);
        handleShowTooltipData(!!isNewMissionCompleted);

        setIsTooltipVisible(false);
    }, [isAllMissionsCompleted, isNewMissionCompleted, dailyMissionCompletedCount, dailyMissionTotalCount, isNewMissionCompletedOnMounted]);

    useEffect(() => {
        if (!isNewMissionCompleted) {
            setIsForceCheckedIcon(false);
        }
    }, [isNewMissionCompleted]);

    const handleTooltipClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        dispatch(toggleIsNewMission(false));
        setIsForceCheckedIcon(false);
    }, [dispatch, toggleIsNewMission]);

    useEffect(() => {
        if (isTooltipVisible) {
            SoundManager.play('missionCompletedSFX');
        }
    }, [isTooltipVisible]);

    if (!dailyMission) {
        return null;
    }

    return (
        <div id="closing-ignore-el" style={isSelected ? { background: 'rgba(31, 31, 31, 0.40)' } : {}} ref={ref} className={`mission-button mission-button--${type} w-100 d-flex justify-content-center ${type === 'secondary' ? 'side-bar-button--secondary-wrapper' : 'side-bar-button--wrapper'}`}>
            <button 
                id="missionsButton" 
                onClick={handleMissionsClick}
                className={`${type === 'secondary' ? 'side-bar-button side-bar-button--secondary' : 'side-bar-button'} position-relative d-flex align-items-center justify-content-center w-100`} 
            >
                {missionsIcon}

                <span>
                    {translate('menu_missions')}
                </span>
            </button>

            {!isMissionsVisited && <div className={`mission-button-indicator ${type === 'secondary' ? 'mission-button-indicator--secondary' : ''}`}/>}

            <div onClick={handleTooltipClick} className={`mission-button-tooltip mission-button-tooltip--${type} ${isTooltipVisible ? 'mission-button-tooltip--visible' : 'mission-button-tooltip--invisible'}`}>
                {tooltipText}
                <svg className={`mission-button-tooltip-arrow--${type}`} xmlns="http://www.w3.org/2000/svg" width="7" height="12" viewBox="0 0 7 12" fill="none">
                    <path d="M0 0L5.86667 4.4C6.93333 5.2 6.93333 6.8 5.86667 7.6L0 12V0Z" fill="currentColor"/>
                </svg>
            </div>
        </div>
    );
};